package top.wcpe.allincore.ui.web

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLCanvasElement
import top.wcpe.allincore.dsl.compiler.pipeline.CompilationPipeline
import top.wcpe.allincore.dsl.compiler.pipeline.CompileResult
import top.wcpe.allincore.layout.engine.AvailableSpace
import top.wcpe.allincore.layout.engine.Box
import top.wcpe.allincore.layout.engine.Dimension
import top.wcpe.allincore.layout.engine.Edges
import top.wcpe.allincore.layout.engine.LayoutMode
import top.wcpe.allincore.layout.engine.LayoutResult
import top.wcpe.allincore.layout.engine.defaultLayoutEngine
import top.wcpe.allincore.ui.runtime.element.Column
import top.wcpe.allincore.ui.runtime.element.EntityPreview
import top.wcpe.allincore.ui.runtime.element.ItemStack
import top.wcpe.allincore.ui.runtime.element.Row
import top.wcpe.allincore.ui.runtime.element.Slot
import top.wcpe.allincore.ui.runtime.element.Text
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.event.FocusManager
import top.wcpe.allincore.ui.runtime.event.InputTraceCollector
import top.wcpe.allincore.ui.runtime.event.SessionHitRegistry
import top.wcpe.allincore.ui.runtime.ir.DslExecutor
import top.wcpe.allincore.ui.runtime.ir.IrInterpreter
import top.wcpe.allincore.ui.runtime.ir.call.ActionDispatcher
import top.wcpe.allincore.ui.runtime.ir.request.RequestRegistry
import top.wcpe.allincore.ui.runtime.ir.state.SharedStateRegistry
import top.wcpe.allincore.ui.runtime.render.RendererRegistry
import top.wcpe.allincore.ui.runtime.render.UIRenderer
import top.wcpe.allincore.ui.web.asset.AssetSource
import top.wcpe.allincore.ui.web.asset.AssetSourceAdapter
import top.wcpe.allincore.ui.web.asset.BrowserAssetFetchBackend
import top.wcpe.allincore.ui.web.asset.BrowserAssetObjectUrlBackend
import top.wcpe.allincore.ui.web.asset.BrowserJpegIntegrityChecker
import top.wcpe.allincore.ui.web.asset.HttpAssetSourceAdapter
import top.wcpe.allincore.ui.web.asset.JsAssetUriBackend
import top.wcpe.allincore.ui.web.asset.PreviewAssetResolver
import top.wcpe.allincore.ui.web.asset.PreviewDependencyScanner
import top.wcpe.allincore.ui.web.mock.MockRequestDispatcher
import top.wcpe.allincore.ui.web.mock.parseMockScenario
import top.wcpe.allincore.ui.web.model.ResolverThreeModelAssetLoader
import top.wcpe.allincore.ui.web.model.ThreeModelCompositionHost
import top.wcpe.allincore.ui.web.model.defaultThreeModuleLoader
import top.wcpe.allincore.ui.web.docs.DocsApp
import top.wcpe.allincore.ui.web.docs.DocsDependencyRegistry
import top.wcpe.allincore.ui.web.docs.DocsPreviewDependencies
import top.wcpe.allincore.ui.web.docs.WikiMuseumDependencyRegistry
import top.wcpe.allincore.ui.web.docs.docsPreviewDependenciesFactory
import top.wcpe.allincore.ui.web.preview.DirectorySelection
import top.wcpe.allincore.ui.web.preview.PreviewApp
import top.wcpe.allincore.ui.web.preview.PreviewBackground
import top.wcpe.allincore.ui.web.preview.PreviewCandidateLoader
import top.wcpe.allincore.ui.web.preview.PreviewCandidateSource
import top.wcpe.allincore.ui.web.preview.PreviewSourceRuntime
import top.wcpe.allincore.ui.web.preview.PreviewSourceRuntimeRouter
import top.wcpe.allincore.ui.web.preview.installOpenDirectoryButton
import top.wcpe.allincore.ui.web.preview.queryParam
import kotlin.js.json

private const val CANVAS_W = 400
private const val CANVAS_H = 400

/**
 * Web 预览器 demo 入口（FR-UI-062 首切片 + FR-UI-060 浏览器腿）：手构 UIElement 树，
 * 经 [defaultLayoutEngine]（JS actual = Rust taffy wasm）计算真实布局，
 * 再由 [UIRenderer]（内含 commonMain 6 层缓冲）回放到 [PixiRenderBridge]。
 *
 * 启动序：先异步初始化 taffy-wasm（web 目标 glue 的 default init()，wasm 经 fetch 实例化），
 * 就绪后才初始化 Pixi 并渲染——保证 layout-engine 的同步 external 调用时 wasm 实例已挂上。
 */
fun main() {
    initializeWebPreviewRuntime().then { startApp() }.catch { reason ->
        console.error("WEB_LAYOUT_INIT_FAILED：Web 布局引擎初始化失败，预览未启动；原因=${reason.toString()}")
        Unit
    }
}

/** 动态初始化生产预览使用的 taffy-wasm，供真实 Browser 测试复用同一启动接缝。 */
internal fun initializeWebPreviewRuntime(): kotlin.js.Promise<Unit> {
    val loading: dynamic = js("import('taffy-wasm').then(function(m){ return m.default(); })")
    return loading.unsafeCast<kotlin.js.Promise<Unit>>()
}

private fun startApp() {
    val body = document.body ?: return
    val search = window.location.search
    val hash = window.location.hash
    // FR-UI-069：?mode=docs 或 hash #/components 进入组件文档站
    if (isDocsMode(search, hash)) {
        startDocsApp(body)
        return
    }
    // UI 博物馆：?mode=museum / #/museum → 直接加载 wiki/index
    val museum = isMuseumMode(search, hash)
    val src = when {
        museum -> "examples/wiki/index.allin"
        else -> queryParam(search, "src")
    }
    val w = queryParam(search, "w")?.toIntOrNull() ?: if (museum) 960 else CANVAS_W
    val h = queryParam(search, "h")?.toIntOrNull() ?: if (museum) 640 else CANVAS_H
    val backgroundReference = queryParam(search, "bg")?.takeIf(String::isNotBlank)
    val app = Application()
    val dependencies = createPreviewRuntimeDependencies(backgroundReference)
    val surface = WebRenderSurface(
        "web-preview",
        DomWebRenderHost(body),
        applicationFactory = { PixiWebRenderApplication(app) },
        initialWidth = w,
        initialHeight = h,
        modelCompositionHost = dependencies.modelCompositionHost,
    )
    bootstrapWebPreview(surface) {
        val background = PreviewBackground(surface, w, h)
        val preview = PreviewApp(
            app,
            w,
            h,
            background,
            dependencies.candidateSource,
            surface,
            dependencies.directoryRuntimeFactory,
        )
        if (src != null) {
            preview.start(src)
        } else if (surface.state == WebRenderSurfaceState.READY) {
            launchScene(surface)
            preview.onTakeOverCanvas = { surface.uninstallInputAdapter() }
        } else {
            preview.start("")
        }
        installOpenDirectoryButton(preview)
        window.addEventListener("beforeunload", {
            preview.dispose()
            background.dispose()
            surface.dispose()
        })
    }
}

/** docs 模式：文档壳复用 PreviewApp/resolver，不另造引擎；注入组件 alias 登记表。 */
private fun startDocsApp(body: dynamic) {
    val host = document.createElement("div")
    body.appendChild(host)
    docsPreviewDependenciesFactory = {
        val dependencies = createPreviewRuntimeDependencies(
            backgroundReference = null,
            docsComponentRegistry = true,
        )
        DocsPreviewDependencies(
            candidateSource = dependencies.candidateSource,
            modelCompositionHost = dependencies.modelCompositionHost,
            directoryRuntimeFactory = dependencies.directoryRuntimeFactory,
        )
    }
    val docsApp = DocsApp(host.unsafeCast<org.w3c.dom.HTMLElement>())
    docsApp.start()
    window.addEventListener("beforeunload", {
        docsApp.dispose()
        docsPreviewDependenciesFactory = null
    })
}

internal fun isDocsMode(search: String, hash: String): Boolean {
    val mode = queryParam(search, "mode")?.lowercase()
    if (mode == "docs") return true
    val cleaned = hash.removePrefix("#").trim()
    return cleaned == "/components" || cleaned.startsWith("/components/")
}

/** UI 博物馆入口：?mode=museum 或 hash #/museum。 */
internal fun isMuseumMode(search: String, hash: String): Boolean {
    val mode = queryParam(search, "mode")?.lowercase()
    if (mode == "museum" || mode == "wiki") return true
    val cleaned = hash.removePrefix("#").trim()
    return cleaned == "/museum" || cleaned.startsWith("/museum/") ||
        cleaned == "/wiki" || cleaned.startsWith("/wiki/")
}

private data class PreviewRuntimeDependencies(
    val candidateSource: PreviewCandidateSource,
    val modelCompositionHost: ModelCompositionHost,
    val directoryRuntimeFactory: (DirectorySelection) -> PreviewSourceRuntime,
)

/** URL 与目录各自拥有 resolver runtime，surface 只持有统一路由宿主。 */
private fun createPreviewRuntimeDependencies(
    backgroundReference: String?,
    docsComponentRegistry: Boolean = false,
): PreviewRuntimeDependencies {
    val initial = createPreviewSourceRuntime(
        httpPreviewSource(),
        backgroundReference,
        docsComponentRegistry = docsComponentRegistry,
    )
    val router = PreviewSourceRuntimeRouter(initial)
    return PreviewRuntimeDependencies(
        router,
        router,
        { selection -> createDirectoryRuntime(selection, backgroundReference) },
    )
}

private fun httpPreviewSource(): AssetSourceAdapter = HttpAssetSourceAdapter(
    AssetSource.URL,
    previewAssetBaseUri(),
    JsAssetUriBackend,
    BrowserAssetFetchBackend(),
)

private fun createDirectoryRuntime(
    selection: DirectorySelection,
    backgroundReference: String?,
): PreviewSourceRuntime {
    val source = selection.createAssetSource()
    selection.dispose()
    return try {
        createPreviewSourceRuntime(source, backgroundReference, docsComponentRegistry = false)
    } catch (error: Throwable) {
        source.dispose()
        throw error
    }
}

private fun createPreviewSourceRuntime(
    source: AssetSourceAdapter,
    backgroundReference: String?,
    docsComponentRegistry: Boolean,
): PreviewSourceRuntime {
    val objectUrls = BrowserAssetObjectUrlBackend()
    val resolver = PreviewAssetResolver(source, objectUrls, BrowserJpegIntegrityChecker(objectUrls))
    val modelHost = ThreeModelCompositionHost(
        defaultThreeModuleLoader(),
        ResolverThreeModelAssetLoader(resolver, objectUrls),
    )
    // 预览与 docs 都注入博物馆/文档依赖表；并注入全部已登记主题
    val scanner = if (docsComponentRegistry) {
        PreviewDependencyScanner(
            resolver,
            registry = DocsDependencyRegistry.create(),
            includeAllRegisteredThemes = true,
        )
    } else {
        PreviewDependencyScanner(
            resolver,
            registry = WikiMuseumDependencyRegistry.create(),
            includeAllRegisteredThemes = true,
        )
    }
    val candidate = PreviewCandidateLoader(
        resolver,
        scanner,
        backgroundReference = backgroundReference,
        modelPreparer = modelHost,
    )
    return PreviewSourceRuntime(candidate, modelHost)
}

private fun previewAssetBaseUri(): String = js("new globalThis.URL('.', document.baseURI).href") as String

internal fun bootstrapWebPreview(
    surface: WebRenderSurface,
    launch: () -> Unit,
): kotlin.js.Promise<Unit> = surface.init().then {
    launch()
    Unit
}.catch { reason ->
    console.error("WEB_RENDER_INIT_FAILED：WebGL 渲染区域不可用，预览宿主继续启动；原因=${reason.toString()}")
    launch()
    Unit
}

/** 首次真实 wasm 布局成功后，暴露冻结的 JSON 状态供 Playwright 读取。 */
internal fun publishLayoutBackendReady(root: UIElement<*>, layout: LayoutResult) {
    val rootBox = layout.boxOf(root) ?: return
    val child = firstNonTrivialLayoutChild(root, layout) ?: return
    if (rootBox.width() <= 0f || rootBox.height() <= 0f) return

    val objectApi: dynamic = js("Object")
    val childBox = child.second
    val state = objectApi.freeze(
        json(
            "backend" to "taffy-wasm",
            "ready" to true,
            "root" to frozenBoxState(objectApi, root.id(), rootBox),
            "child" to frozenBoxState(objectApi, child.first.id(), childBox),
        ),
    )
    objectApi.defineProperty(
        window,
        "__allincoreWebLayoutBackend",
        json("value" to state, "writable" to false, "configurable" to true, "enumerable" to true),
    )
}

private fun firstNonTrivialLayoutChild(root: UIElement<*>, layout: LayoutResult): Pair<UIElement<*>, Box>? {
    for (child in root.children()) {
        val box = layout.boxOf(child)
        if (box != null && box.width() > 0f && box.height() > 0f) return child to box
        val nested = firstNonTrivialLayoutChild(child, layout)
        if (nested != null) return nested
    }
    return null
}

private fun frozenBoxState(objectApi: dynamic, id: String?, box: Box): dynamic = objectApi.freeze(
    json("id" to id, "x" to box.x(), "y" to box.y(), "width" to box.width(), "height" to box.height()),
)

/**
 * 构树一次 + 可重入渲染 + 挂 DOM 输入适配器（FR-UI-063 Step E）：
 * 输入事件经既有 InputDispatcher 分发改元素状态，适配器合帧后重新布局 / 重绘。
 */
private fun launchScene(surface: WebRenderSurface) {
    var root = if (irDemoRequested()) buildInterpreterRoot() else buildSampleTree()
    var layout: LayoutResult? = null
    var focusManager = FocusManager()
    val hitRegistry = SessionHitRegistry<WebInputOwnerHandler>()
    val inputTrace = InputTraceCollector()
    window.asDynamic().__allincoreInputTrace = { inputTrace.snapshot().toTypedArray() }

    fun renderScene() {
        surface.resize(CANVAS_W, CANVAS_H)
        surface.setLogicalViewport(CANVAS_W.toFloat(), CANVAS_H.toFloat(), 1f)
        val computed = defaultLayoutEngine()
            .compute(root, AvailableSpace.definite(CANVAS_W.toFloat(), CANVAS_H.toFloat()))
        layout = computed
        publishLayoutBackendReady(root, computed)
        surface.renderFrame { bridge ->
            UIRenderer.render(root, computed, bridge, RendererRegistry.defaultRegistry())
        }
    }

    renderScene()
    val inputAdapter = WebInputAdapter(
        surface.canvas.unsafeCast<HTMLCanvasElement>(),
        { root },
        { layout },
        ::renderScene,
        focusManagerProvider = { focusManager },
        hitRegistryProvider = { hitRegistry },
        traceSinkProvider = { inputTrace },
    )
    surface.installInputAdapter(inputAdapter)
    if (irDemoRequested()) {
        // FR-UI-064 验收锚：重建 session（session 状态回默认、persist 保留——预览重载清空约定）
        window.asDynamic().__irRebuild = {
            inputAdapter.resetSession()
            inputTrace.clear()
            focusManager = FocusManager()
            root = buildInterpreterRoot()
            renderScene()
        }
    }
}

/**
 * 构建样本树：标题 + 三张 flexGrow=1 等分卡片 + 说明文本。
 * 卡片宽度由 taffy 按容器内容宽等分（非手排坐标可为）——布局为真的直接证据。
 * 中间卡片挂 onClick 换色——输入链路（DOM → InputDispatcher → 状态 → 重绘）为真的直接证据。
 */
private fun buildSampleTree(): UIElement<*> {
    val root = Column.create()
    root.style().backgroundColor(0xFF1E1F26.toInt())
    root.style().layout().mode(LayoutMode.COLUMN)
        .width(Dimension.px(CANVAS_W.toFloat())).height(Dimension.px(CANVAS_H.toFloat()))
        .padding(Edges.all(16f)).gap(14f)

    val title = Text.create().text("AllinCore Web 预览 · PixiJS + taffy wasm")
    title.style().foregroundColor(0xFFE8E8F0.toInt())
    title.style().layout().width(Dimension.percent(100f)).height(Dimension.px(20f))

    val cards = Row.create()
    cards.style().backgroundColor(0xFF2A2C36.toInt())
    cards.style().layout().mode(LayoutMode.ROW)
        .width(Dimension.percent(100f)).height(Dimension.px(120f))
        .padding(Edges.all(12f)).gap(12f)
    listOf(0xFF4C8DFF.toInt(), 0xFF57C56E.toInt(), 0xFFE0B345.toInt()).forEachIndexed { index, color ->
        val card = top.wcpe.allincore.ui.runtime.element.Box.create()
        card.style().backgroundColor(color)
        card.style().layout().flexGrow(1f).height(Dimension.percent(100f))
        if (index == 1) {
            // 中间卡片：点击在原色与高亮红之间切换（handler 只改状态，重绘由适配器合帧统一做）；
            // cursor 声明经 InputDispatcher.applyCursor → CursorSink(js) 落到 document.body.style.cursor
            card.id("card-toggle")
            card.cursor("pointer")
            var toggled = false
            card.onClick {
                toggled = !toggled
                card.style().backgroundColor(if (toggled) 0xFFE05A5A.toInt() else color)
            }
        }
        cards.child(card)
    }

    // FR-UI-062 占位展示：item / entity / slot 元素经 commonMain renderer → PixiRenderBridge 画可辨识占位框
    val showcase = Row.create()
    showcase.style().backgroundColor(0xFF23252F.toInt())
    showcase.style().layout().mode(LayoutMode.ROW)
        .width(Dimension.percent(100f)).height(Dimension.px(72f))
        .padding(Edges.all(12f)).gap(12f)

    val sword = ItemStack.create().itemId("minecraft:diamond_sword").count(1)
    sword.style().layout().width(Dimension.px(48f)).height(Dimension.px(48f))

    val apples = ItemStack.create().itemId("minecraft:golden_apple").count(64)
    apples.style().layout().width(Dimension.px(48f)).height(Dimension.px(48f))

    val slot = Slot.create()
        .staticItem(ItemStack.create().itemId("minecraft:emerald").count(16))
        .slotSize(48f)
        .borderColor(0xFF5A5C6A.toInt())
        .borderWidth(1f)
    slot.style().layout().width(Dimension.px(48f)).height(Dimension.px(48f))

    val entity = EntityPreview.create().source("entity:zombie").entityType("minecraft:zombie")
    entity.style().layout().width(Dimension.px(48f)).height(Dimension.px(48f))

    showcase.child(sword).child(apples).child(slot).child(entity)

    val caption = Text.create().text("item / entity / slot 占位框（PixiJS 渲染桥）| 点击中间卡片换色")
    caption.style().foregroundColor(0xFF9AA0B0.toInt())
    caption.style().layout().width(Dimension.percent(100f)).height(Dimension.px(16f))

    root.child(title).child(cards).child(showcase).child(caption)
    return root
}

/** FR-UI-064 spike：URL 带 ?demo=ir 时改走「.allin 源码 → 浏览器内编译 → IR 解释器」闭环。 */
private fun irDemoRequested(): Boolean = window.location.search.contains("demo=ir")

/**
 * FR-UI-064 demo .allin：state/persist + net.request 三剧本（成功·带延时与参数分支 / 错误 / 超时）。
 * 布局：column 竖排两行状态文本 + row 五按钮（坐标可预测，供 playwright 定点点击）。
 */
private val IR_DEMO_SOURCE = """
    screen {
        state defaults {
            count = 0
            msg = "未加载"
        }
        persist defaults {
            keep = 0
        }
        fun increment() {
            state.count = state.count + 1
        }
        fun keepMore() {
            persist.keep = persist.keep + 1
        }
        suspend fun load() {
            state.msg = "加载中"
            var r = await net.request("api.hello", #{"page": 1})
            state.msg = r.payload.string("msg")
        }
        suspend fun loadFail() {
            state.msg = "加载中"
            var r = await net.request("api.fail", #{})
            if (r.kind == "success") {
                state.msg = "不应成功"
            } else {
                state.msg = "失败已捕获"
            }
        }
        suspend fun loadSlow() {
            state.msg = "加载中"
            var r = await net.request("api.slow", #{})
            if (r.kind == "timeout") {
                state.msg = "已超时"
            } else {
                state.msg = "不应回包"
            }
        }
        root {
            column "c" {
                width = 400
                height = 400
                padding = 12
                gap = 10
                text "t1" {
                    text = "计数 ${'$'}{state.count} · keep ${'$'}{persist.keep}"
                    width = 376
                    height = 20
                }
                text "t2" {
                    text = "msg: ${'$'}{state.msg}"
                    width = 376
                    height = 20
                }
                row "btns" {
                    width = 376
                    height = 40
                    gap = 6
                    button "b1" {
                        text = "+1"
                        width = 60
                        height = 40
                        onClick = ::increment
                    }
                    button "b2" {
                        text = "keep"
                        width = 60
                        height = 40
                        onClick = ::keepMore
                    }
                    button "b3" {
                        text = "加载"
                        width = 70
                        height = 40
                        onClick = ::load
                    }
                    button "b4" {
                        text = "失败"
                        width = 70
                        height = 40
                        onClick = ::loadFail
                    }
                    button "b5" {
                        text = "超时"
                        width = 70
                        height = 40
                        onClick = ::loadSlow
                    }
                }
            }
        }
    }
""".trimIndent()

/** 内嵌 mock 声明（JSON 约定见 docs/specs/preview-mock-data-layer.md；027 换真文件加载零改机制）。 */
private val MOCK_SCENARIO_JSON = """
    {
      "routes": {
        "api.hello": {
          "match": [
            { "when": { "page": 1 }, "response": { "msg": "你好·分支页1" }, "delayMs": 300 }
          ],
          "response": { "msg": "你好·默认" }
        },
        "api.fail": { "error": { "reason": "维护中" } },
        "api.slow": { "timeout": true, "delayMs": 200 }
      }
    }
""".trimIndent()

/**
 * 浏览器内编译 + 解释：诊断打 console；session 计数暴露到 window.__irCount 供 playwright 断言。
 * executor 用同步模式——点击 handler 立即改 state，输入适配器合帧重绘即拿到新文本。
 * 编译失败时回退手构树（console.error 已列出诊断，spike 迭代用）。
 */
private fun buildInterpreterRoot(): UIElement<*> {
    val result = CompilationPipeline().compile(IR_DEMO_SOURCE, "web-preview/ir-spike.allin")
    if (result is CompileResult.Failure) {
        result.diagnostics.forEach { console.error("[ir-spike] 编译诊断: " + it) }
        return buildSampleTree()
    }
    val success = result as CompileResult.Success
    success.diagnostics.forEach { console.warn("[ir-spike] 诊断: " + it) }
    val mockDispatcher = MockRequestDispatcher(parseMockScenario(MOCK_SCENARIO_JSON))
    val registry = RequestRegistry()
    val session = IrInterpreter.interpretSession(
        success.ir,
        ActionDispatcher.NOOP,
        mockDispatcher,
        registry,
        null,
        null,
        null,
        DslExecutor.synchronous(),
        null,
    )
    mockDispatcher.bind(registry) { session.evalContext }
    val countSignal = session.states["count"]
    val msgSignal = session.states["msg"]
    val win = window.asDynamic()
    win.__irCount = { countSignal?.get() }
    win.__irMsg = { msgSignal?.get() }
    win.__irKeep = { SharedStateRegistry.snapshotPersist()["keep"]?.get() }
    console.log("[ir-spike] 解释器会话就绪 count=" + countSignal?.get())
    return session.root
}
